/* Motion for the Goodyear editorial page.

   Progressive by design, matching sneaker-landing.js: the markup renders fully without this file.
   Reveal, parallax and tilt are only attached when IntersectionObserver exists and the visitor has
   not asked for reduced motion. Everything unwinds in stop() so Shopify theme-editor section
   reloads re-register cleanly. */
if (!customElements.get('goodyear-landing')) {
  customElements.define('goodyear-landing', class extends HTMLElement {
    connectedCallback() {
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.onPreference = () => this.start();
      this.motion.addEventListener('change', this.onPreference);
      this.start();
    }

    start() {
      this.stop();
      if (this.motion.matches || !('IntersectionObserver' in window)) return;

      this.reveals();
      this.parallax();
      this.counter();
      // Tilt is pointer-driven, so it is pointless (and jumpy) on touch.
      if (this.fine.matches) this.tilt();
    }

    /* ---- Scroll reveals ---- */

    reveals() {
      // .gw-hero__heading is deliberately absent from this list: its words carry their own rise,
      // and a block fade on top of that would double up. It gets a bare `is-in` in headline().
      this.headline();

      const targets = this.querySelectorAll(
        '.gw-hero__eyebrow, .gw-hero__intro, .gw-principles > span, .gw-story__intro > *,' +
        '.gw-detail, .gw-feature__body > *, .gw-section-head, .gw-products__item,' +
        '.gw-guarantee__number, .gw-guarantee__body > *, .gw-closing > *'
      );
      if (!targets.length) return;

      targets.forEach((el) => el.classList.add('gw-reveal'));

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          this.observer.unobserve(el);
          // Stagger within a row so a grid cascades rather than popping in at once.
          const siblings = Array.from(el.parentElement ? el.parentElement.children : [el]);
          const delay = Math.min(siblings.indexOf(el), 5) * 90;
          el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-in');
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      targets.forEach((el) => this.observer.observe(el));

      // Keyboard users must never land on a still-hidden control.
      this.onFocus = (event) => {
        const hidden = event.target.closest('.gw-reveal:not(.is-in)');
        if (hidden) hidden.classList.add('is-in');
      };
      this.addEventListener('focusin', this.onFocus);
    }

    /* ---- Headline ----
       The words sit translated out of view until the heading block is marked `is-in`. No
       .gw-reveal here, so the block itself never fades; only the words move. */

    headline() {
      const heading = this.querySelector('.gw-hero__heading');
      if (!heading) return;

      // Adding the class here, not in Liquid, keeps the heading visible if this script never runs.
      heading.classList.add('gw-headline');
      this.headingEl = heading;
      this.headlineObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          heading.classList.add('is-in');
        });
      }, { threshold: 0.2 });
      this.headlineObserver.observe(heading);
    }

    /* ---- Parallax ----
       Depth on the two editorial images and the hero furniture. Values are written to custom
       properties and composited by CSS transforms, so the scroll handler never reads layout. */

    parallax() {
      // Targeted by class rather than a data attribute: these images come from Shopify's
      // image_tag filter, which does not reliably pass custom attributes through.
      this.layers = Array.from(this.querySelectorAll(
        '.gw-hero__image, .gw-feature__image'
      ));
      if (!this.layers.length) return;

      // Only move layers that are actually on screen.
      this.visible = new Set();
      this.parallaxObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        });
        this.queue();
      }, { rootMargin: '15% 0px' });
      this.layers.forEach((el) => this.parallaxObserver.observe(el));

      this.onScroll = () => this.queue();
      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onScroll, { passive: true });
      this.queue();
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const height = innerHeight || 1;
        this.visible.forEach((el) => {
          const box = el.getBoundingClientRect();
          // -1 when the element sits below the fold, 1 when it has scrolled above it.
          const progress = Math.max(-1, Math.min(1, (box.top + box.height / 2 - height / 2) / height * -2));
          el.style.setProperty('--gw-shift', progress.toFixed(4));
        });
      });
    }

    /* ---- Guarantee counter ----
       The 25 counts up once, the first time the panel is reached. */

    counter() {
      const number = this.querySelector('[data-gw-count]');
      if (!number) return;

      const target = parseInt(number.dataset.gwCount, 10);
      if (!Number.isFinite(target)) return;

      const value = number.querySelector('[data-gw-count-value]') || number;
      const original = value.textContent;

      this.countObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          const start = performance.now();
          const duration = 1400;
          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // Ease out so it settles rather than stopping dead.
            const eased = 1 - Math.pow(1 - t, 3);
            value.textContent = Math.round(target * eased);
            if (t < 1) this.countFrame = requestAnimationFrame(step);
            else value.textContent = original;
          };
          this.countFrame = requestAnimationFrame(step);
        });
      }, { threshold: 0.4 });

      this.countObserver.observe(number);
      this.countValue = value;
      this.countOriginal = original;
    }

    /* ---- 3D tilt ----
       Product tiles and the editorial image lean toward the cursor. The tilt is applied to the
       page's own wrapper elements, never to product-card internals, which run their own
       translateX image carousel. */

    tilt() {
      this.tiltTargets = Array.from(this.querySelectorAll('[data-gw-tilt]'));
      if (!this.tiltTargets.length) return;

      this.onTiltMove = (event) => {
        const el = event.currentTarget;
        const box = el.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width - 0.5;
        const y = (event.clientY - box.top) / box.height - 0.5;
        el.style.setProperty('--gw-tilt-x', (y * -7).toFixed(2) + 'deg');
        el.style.setProperty('--gw-tilt-y', (x * 9).toFixed(2) + 'deg');
        el.style.setProperty('--gw-glare-x', ((x + 0.5) * 100).toFixed(2) + '%');
        el.style.setProperty('--gw-glare-y', ((y + 0.5) * 100).toFixed(2) + '%');
      };

      this.onTiltLeave = (event) => {
        const el = event.currentTarget;
        el.style.removeProperty('--gw-tilt-x');
        el.style.removeProperty('--gw-tilt-y');
      };

      this.tiltTargets.forEach((el) => {
        el.classList.add('gw-tilt');
        el.addEventListener('pointermove', this.onTiltMove);
        el.addEventListener('pointerleave', this.onTiltLeave);
      });
    }

    stop() {
      this.observer?.disconnect();
      this.observer = null;
      this.headlineObserver?.disconnect();
      this.headlineObserver = null;
      this.headingEl?.classList.remove('gw-headline', 'is-in');
      this.headingEl = null;
      this.parallaxObserver?.disconnect();
      this.parallaxObserver = null;
      this.countObserver?.disconnect();
      this.countObserver = null;

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.countFrame) cancelAnimationFrame(this.countFrame);
      this.countFrame = null;
      if (this.countValue) this.countValue.textContent = this.countOriginal;
      this.countValue = null;

      if (this.onFocus) this.removeEventListener('focusin', this.onFocus);
      this.onFocus = null;

      if (this.onScroll) {
        removeEventListener('scroll', this.onScroll);
        removeEventListener('resize', this.onScroll);
        this.onScroll = null;
      }

      this.layers?.forEach((el) => el.style.removeProperty('--gw-shift'));
      this.layers = null;
      this.visible = null;

      this.tiltTargets?.forEach((el) => {
        el.classList.remove('gw-tilt');
        el.removeEventListener('pointermove', this.onTiltMove);
        el.removeEventListener('pointerleave', this.onTiltLeave);
        el.style.removeProperty('--gw-tilt-x');
        el.style.removeProperty('--gw-tilt-y');
        el.style.removeProperty('--gw-glare-x');
        el.style.removeProperty('--gw-glare-y');
      });
      this.tiltTargets = null;

      this.querySelectorAll('.gw-reveal').forEach((el) => {
        el.classList.remove('gw-reveal', 'is-in');
        el.style.transitionDelay = '';
      });
    }

    disconnectedCallback() {
      this.stop();
      this.motion?.removeEventListener('change', this.onPreference);
    }
  });
}
